import { syncBrowserThemeColor } from '../utils/browserSurface';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// V2 retained old preview/manual overrides with no visible system setting on the new shell.
// Start existing browsers in system mode once; preserve explicit choices made from V3 onward.
const STORAGE_KEY = 'POSTPOP_COLOR_SCHEME_V3';
const COLOR_SCHEMES = ['light', 'dark', 'system'];
export const NEXT_COLOR_SCHEME = { system: 'dark', dark: 'light', light: 'system' };

const ColorSchemeContext = createContext({
  colorScheme: 'system',
  resolvedMode: 'light',
  setColorScheme: () => {},
  toggleColorScheme: () => {},
});

const getSystemMode = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialScheme = () => {
  if (typeof window === 'undefined') {
    return 'system';
  }
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return COLOR_SCHEMES.includes(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
};

export const ColorSchemeProvider = ({ children }) => {
  const [colorScheme, setColorSchemeState] = useState(getInitialScheme);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    const resume = () => setSystemMode(getSystemMode());
    const storage = (event) => {
      if (event.key === STORAGE_KEY || event.key === null) setColorSchemeState(getInitialScheme());
    };
    resume();
    window.addEventListener('focus', resume);
    window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener('focus', resume);
      window.removeEventListener('pageshow', resume);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('storage', storage);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const resolvedMode = colorScheme === 'system' ? systemMode : colorScheme;

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
    document.documentElement.dataset.colorScheme = colorScheme;
    document.documentElement.style.colorScheme = resolvedMode;

    syncBrowserThemeColor();
  }, [resolvedMode, colorScheme]);

  const setColorScheme = useCallback((nextScheme) => {
    const normalized = COLOR_SCHEMES.includes(nextScheme) ? nextScheme : 'system';
    setColorSchemeState(normalized);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, normalized);
      } catch {
        /* Private storage may be unavailable. */
      }
    }
  }, []);

  const toggleColorScheme = useCallback(() => {
    // Include a return to the OS preference after an explicit appearance choice.
    setColorScheme(NEXT_COLOR_SCHEME[colorScheme]);
  }, [colorScheme, setColorScheme]);

  const value = useMemo(
    () => ({
      colorScheme,
      resolvedMode,
      setColorScheme,
      toggleColorScheme,
    }),
    [colorScheme, resolvedMode, setColorScheme, toggleColorScheme],
  );

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
};

export const useColorScheme = () => useContext(ColorSchemeContext);
